/**
 * RelationCombobox — Pollen-Dots-curated relation picker that doubles as
 * a free-text input. Reuses Shadcn's Popover + Command primitives so the
 * dropdown feels native on mobile.
 *
 * Why custom: every prior turn shipped a plain <Input> for relations,
 * which left "Spouse", "spouse", "wife", and "Wife" all sitting in the
 * DB as different keys. With curated options + still-typeable freetext
 * we get consistent keys for the common 90% while never blocking the
 * operator from typing an unusual relation.
 *
 * Props mirror a plain Input so it slots into existing rows:
 *   value, onChange(v), onBlur, placeholder, testId, className
 */
import React, { useState } from 'react';
import { Input } from './ui/input';
import { ChevronDown } from 'lucide-react';

const PRESETS = [
  'Self', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter',
  'Brother', 'Sister', 'Friend', 'Cousin', 'Uncle', 'Aunt',
  'Grandparent', 'Grandchild', 'Colleague', 'Father-in-law',
  'Mother-in-law', 'Brother-in-law', 'Sister-in-law', 'Other',
];

export default function RelationCombobox({
  value, onChange, onBlur, placeholder = 'e.g. Spouse, Daughter',
  className = '', testId,
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-1">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => { onBlur && onBlur(e); setTimeout(close, 150); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="h-8 text-sm flex-1"
          data-testid={testId}
          autoComplete="off"
        />
        <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpen(s => !s)}
                className="h-8 w-8 inline-flex items-center justify-center rounded
                           border bg-white hover:bg-amber-50"
                data-testid={`${testId}-toggle`}>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40
                        bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
             data-testid={`${testId}-dropdown`}>
          {PRESETS
            .filter(r => !value || r.toLowerCase().includes((value || '').toLowerCase()))
            .map((r, i) => (
              <button type="button" key={r}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { onChange(r); setOpen(false); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50
                                 border-b last:border-b-0"
                      data-testid={`${testId}-option-${i}`}>
                {r}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
